class CustomCartDrawerApp {
  constructor() {
    this.sectionId = 'custom-cart-drawer';
    this.isDrawerOpen = false;
    this.returnFocusElement = null;
    this.lastResult = null;
    this.desiredQuantities = new Map();
    this.mutationQueue = Promise.resolve();
    this.noteTimer = null;
    this.checkoutInProgress = false;
    this.hasStartedSync = false;
    this.rewardHistory = null;
    this.rewardProgress = null;
    this.rewardResizeObserver = null;
    this.rewardJourneyState = null;
    this.rewardAnimationToken = 0;
    this.activeRewardAnimations = new Set();
    this.activeUpsellPicker = null;
    this.activeUpsellCard = null;
    this.upsellPickerOpener = null;

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentChange = this.onDocumentChange.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onItemAdded = this.onItemAdded.bind(this);
    this.onExternalCartUpdate = this.onExternalCartUpdate.bind(this);
    this.onPageShow = this.onPageShow.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);

    this.refreshDOMReferences();
    this.configure();
    this.initializeRewardHistory();
    this.bindEvents();
    this.scheduleInitialSync();
  }

  scheduleInitialSync() {
    const start = () => {
      if (this.hasStartedSync) return;
      this.enqueueMutation(() => this.sync({ render: false }))
        .catch((error) => this.handleError(error));
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(start, { timeout: 1800 });
    } else {
      window.setTimeout(start, 900);
    }
  }

  bindEvents() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('change', this.onDocumentChange);
    document.addEventListener('keydown', this.onDocumentKeydown);
    document.addEventListener('cart:item-added', this.onItemAdded);
    document.addEventListener('cart:updated', this.onExternalCartUpdate);
    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('shopify:section:unload', this.onSectionLoad);
    window.addEventListener('pageshow', this.onPageShow);
    document.addEventListener('cart-drawer:open', (event) => this.open(event.detail?.trigger));
    document.addEventListener('cart-drawer:refresh', () => this.refresh());
  }

  refreshDOMReferences() {
    this.sectionWrapper = document.getElementById(`shopify-section-${this.sectionId}`);
    this.drawer = document.querySelector('[data-custom-cart-drawer]');
    this.panel = this.drawer?.querySelector('[data-cart-drawer-panel]') || null;
    this.liveRegion = this.drawer?.querySelector('[data-cart-live-region]') || null;
    this.errorRegion = this.drawer?.querySelector('[data-cart-error]') || null;
  }

  configure() {
    const configurationElement = this.drawer?.querySelector('[data-cart-drawer-config]');
    if (!configurationElement) return;

    try {
      this.configuration = JSON.parse(configurationElement.textContent);
      if (this.controller) {
        this.controller.updateConfiguration(this.configuration);
      } else if (window.FreeGiftController && window.customCartAPI) {
        this.controller = new window.FreeGiftController(window.customCartAPI, this.configuration);
      }
      window.requestAnimationFrame(() => this.initializeUpsells());
    } catch (error) {
      this.handleError(error);
    }
  }

  initializeUpsells() {
    this.drawer?.querySelectorAll('[data-cart-upsells]').forEach((section) => {
      const track = section.querySelector('[data-upsell-track]');
      if (!track || track.dataset.upsellReady === 'true') return;
      track.dataset.upsellReady = 'true';
      const update = () => {
        const maximum = Math.max(0, track.scrollWidth - track.clientWidth);
        const scrollable = maximum > 2;
        section.classList.toggle('is-scrollable', scrollable);
        const previous = section.querySelector('[data-upsell-scroll="previous"]');
        const next = section.querySelector('[data-upsell-scroll="next"]');
        if (previous) previous.disabled = !scrollable || track.scrollLeft <= 2;
        if (next) next.disabled = !scrollable || track.scrollLeft >= maximum - 2;
      };
      track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
      window.requestAnimationFrame(update);
    });
  }

  open(trigger = document.activeElement) {
    if (!this.drawer) return;
    this.returnFocusElement = trigger instanceof HTMLElement ? trigger : document.activeElement;
    this.setOpenState(true, { focus: false });

    this.enqueueMutation(async () => {
      await this.sync({ render: true, preserveOpen: true });
      this.focusDrawer();
      document.dispatchEvent(new CustomEvent('cart-drawer:opened'));
    }).catch((error) => this.handleError(error));
  }

  close() {
    if (!this.isDrawerOpen) return;
    this.closeUpsellPicker({ restoreFocus: false });
    this.cancelRewardAnimation();
    this.setOpenState(false, { restoreFocus: true });
    document.dispatchEvent(new CustomEvent('cart-drawer:closed'));
  }

  refresh() {
    return this.enqueueMutation(() => this.sync({ render: true, preserveOpen: this.isDrawerOpen }));
  }

  reconcile() {
    return this.enqueueMutation(() => this.sync({ render: false, preserveOpen: this.isDrawerOpen }));
  }

  setOpenState(open, options = {}) {
    this.isDrawerOpen = Boolean(open);
    if (!this.drawer || !this.panel) return;

    this.drawer.classList.toggle('is-open', this.isDrawerOpen);
    this.drawer.setAttribute('aria-hidden', String(!this.isDrawerOpen));
    this.panel.setAttribute('aria-hidden', String(!this.isDrawerOpen));
    document.querySelectorAll('[data-cart-drawer-open], #cart-icon-bubble').forEach((opener) => {
      opener.setAttribute('aria-expanded', String(this.isDrawerOpen));
    });
    document.body.classList.toggle('cart-drawer-open', this.isDrawerOpen);

    if (this.isDrawerOpen) {
      if (options.focus !== false) this.focusDrawer();
    } else if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(options.restoreFocus ? this.returnFocusElement : null);
    } else if (options.restoreFocus && this.returnFocusElement?.isConnected) {
      this.returnFocusElement.focus();
    }
  }

  focusDrawer() {
    if (!this.panel) return;
    const closeButton = this.panel.querySelector('[data-cart-drawer-close]');
    if (typeof window.trapFocus === 'function') {
      window.trapFocus(this.panel, closeButton || this.panel);
    } else {
      (closeButton || this.panel).focus();
    }
  }

  enqueueMutation(operation) {
    const queuedOperation = this.mutationQueue.then(operation, operation);
    this.mutationQueue = queuedOperation.catch(() => undefined);
    return queuedOperation;
  }

  async sync({ cart = null, render = false, preserveOpen = this.isDrawerOpen } = {}) {
    if (!this.controller) return null;
    this.hasStartedSync = true;
    this.configure();
    const authoritativeCart = cart || await window.customCartAPI.fetchCart();
    const result = await this.controller.reconcile(authoritativeCart);
    this.lastResult = result;

    if (render || result.changed) {
      await this.renderSections({ preserveOpen });
    } else {
      this.updateCampaignUI(result);
    }

    document.dispatchEvent(new CustomEvent('custom-cart:updated', {
      detail: { cart: result.cart, source: 'custom-cart-drawer' }
    }));
    return result;
  }

  async renderSections({ preserveOpen = this.isDrawerOpen } = {}) {
    this.closeUpsellPicker({ restoreFocus: false });
    const sectionIds = [this.sectionId, 'cart-icon-bubble'];
    const cartItems = document.getElementById('main-cart-items');
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartItems?.dataset.id) sectionIds.push(cartItems.dataset.id);
    if (cartFooter?.dataset.id) sectionIds.push(cartFooter.dataset.id);

    const sections = await window.customCartAPI.requestSections(sectionIds);
    const parser = new DOMParser();

    if (!sections[this.sectionId]) {
      throw new Error(this.configuration?.strings?.error || window.cartStrings?.error || '');
    }

    if (sections[this.sectionId]) {
      const documentFragment = parser.parseFromString(sections[this.sectionId], 'text/html');
      const incomingWrapper = documentFragment.getElementById(`shopify-section-${this.sectionId}`);
      const currentWrapper = document.getElementById(`shopify-section-${this.sectionId}`);

      if (incomingWrapper && currentWrapper) {
        if (this.isDrawerOpen && Number.isFinite(this.rewardProgress)) {
          incomingWrapper.querySelector('[data-unified-rewards-progress]')?.classList.add('is-layout-pending');
        }
        this.disconnectRewardObserver();
        if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus();
        currentWrapper.innerHTML = incomingWrapper.innerHTML;
      }
    }

    if (sections['cart-icon-bubble']) {
      const documentFragment = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
      const incomingWrapper = documentFragment.getElementById('shopify-section-cart-icon-bubble');
      const iconLink = document.getElementById('cart-icon-bubble');
      if (incomingWrapper && iconLink) iconLink.innerHTML = incomingWrapper.innerHTML;
    }

    [cartItems?.dataset.id, cartFooter?.dataset.id].filter(Boolean).forEach((sectionId) => {
      if (!sections[sectionId]) return;
      const documentFragment = parser.parseFromString(sections[sectionId], 'text/html');
      const incomingWrapper = documentFragment.getElementById(`shopify-section-${sectionId}`);
      const currentWrapper = document.getElementById(`shopify-section-${sectionId}`);
      if (incomingWrapper && currentWrapper) currentWrapper.innerHTML = incomingWrapper.innerHTML;
    });

    this.refreshDOMReferences();
    this.configure();
    this.setOpenState(preserveOpen, { focus: false, restoreFocus: false });
    if (preserveOpen) this.focusDrawer();
    if (this.lastResult) this.updateCampaignUI(this.lastResult);
  }

  updateCampaignUI(result) {
    if (!this.drawer || !result?.state) return;
    const { subtotal } = result.state;
    const cart = result.cart;
    const unavailableIds = new Set(result.unavailableVariantIds || []);
    const expectedTierKeys = new Set(result.state.expectedTiers.map((tier) => tier.key));
    this.updateDebugUI(result);
    const progressElement = this.drawer.querySelector('[data-unified-rewards-progress]');
    if (!progressElement) return;
    const progressBar = progressElement.querySelector('[data-progress-bar]');
    const message = progressElement.querySelector('[data-reward-message]');
    const behavior = this.configuration.outOfStockBehavior;
    const shipping = this.configuration.shipping || {};
    const rewards = [];

    progressElement.querySelectorAll('[data-reward-milestone]').forEach((element) => {
      const type = element.dataset.rewardType;
      let reward = null;

      if (type === 'gift') {
        const tier = this.controller.tiers.find((candidate) => candidate.key === element.dataset.tierKey);
        if (!tier?.valid) {
          element.hidden = true;
          return;
        }
        const threshold = this.controller.thresholdFor(tier, cart);
        const unavailable = tier.available === false || unavailableIds.has(tier.freeVariantId);
        const unlocked = subtotal >= threshold;
        const declined = Boolean((cart.attributes || {})[this.controller.declineAttributeKey(tier.key)]);
        const superseded = this.configuration.mode === 'highest' && unlocked &&
          expectedTierKeys.size > 0 && !expectedTierKeys.has(tier.key);
        const shouldHide = unavailable && behavior !== 'show';
        reward = {
          element,
          id: element.dataset.rewardId || `gift-${tier.key}`,
          type,
          threshold,
          title: tier.title,
          unlocked,
          declined,
          unavailable,
          superseded,
          debugPaid: Boolean(this.configuration.debug && tier.freeVariantPrice > 0),
          lockedMessage: tier.lockedMessage || this.configuration.strings.locked,
          unlockedMessage: tier.unlockedMessage || this.configuration.strings.unlocked
        };
        element.hidden = shouldHide;
        if (shouldHide) return;
      } else if (type === 'shipping' && shipping.enabled) {
        const threshold = this.convertThreshold(Number(shipping.thresholdCents), cart);
        reward = {
          element,
          id: element.dataset.rewardId || 'shipping',
          type,
          threshold,
          title: shipping.label || '',
          unlocked: subtotal >= threshold,
          declined: false,
          unavailable: false,
          superseded: false,
          debugPaid: false,
          lockedMessage: shipping.lockedMessage,
          unlockedMessage: shipping.unlockedMessage
        };
        element.hidden = !(threshold > 0);
        if (!(threshold > 0)) return;
      } else {
        element.hidden = true;
        return;
      }

      rewards.push(reward);
    });

    rewards.sort((first, second) => first.threshold - second.threshold || first.type.localeCompare(second.type));
    progressElement.dataset.rewardCount = String(rewards.length);
    progressElement.hidden = rewards.length === 0;
    if (!rewards.length) {
      this.disconnectRewardObserver();
      return;
    }

    const maxThreshold = Math.max(...rewards.map((reward) => reward.threshold));
    const previousUnlocked = this.rewardHistory === null ? new Set() : new Set(this.rewardHistory);
    const currentUnlocked = new Set(rewards.filter((reward) => reward.unlocked).map((reward) => reward.id));
    const canDetectUnlock = this.rewardHistory !== null;
    const newlyUnlocked = canDetectUnlock
      ? rewards.filter((reward) => reward.unlocked && !previousUnlocked.has(reward.id))
      : [];
    const newlyLocked = canDetectUnlock
      ? rewards.filter((reward) => !reward.unlocked && previousUnlocked.has(reward.id))
      : [];
    const newlyUnlockedGifts = newlyUnlocked.filter((reward) => reward.type === 'gift');
    const droppedGifts = newlyLocked.filter((reward) => reward.type === 'gift');
    const animationTarget = (newlyUnlockedGifts.length ? newlyUnlockedGifts : newlyUnlocked)
      .sort((first, second) => first.threshold - second.threshold)
      .slice(-1)[0] || null;

    rewards.forEach((reward, index) => {
      reward.element.classList.toggle('is-unlocked', reward.unlocked);
      reward.element.classList.toggle('is-unavailable', reward.unavailable);
      reward.element.classList.toggle('is-superseded', reward.superseded);
      reward.element.classList.toggle('is-debug-paid', reward.debugPaid);
      reward.element.classList.toggle('is-declined', reward.declined);
      reward.element.classList.toggle('is-first', index === 0);
      reward.element.classList.toggle('is-last', index === rewards.length - 1);
      reward.element.style.setProperty('--label-stagger', rewards.length > 3 && index % 2 ? '1.15rem' : '0rem');
      reward.element.dataset.rewardState = reward.unavailable
        ? 'nicht verfügbar'
        : reward.declined ? 'erneut verfügbar'
        : reward.unlocked ? 'erreicht' : 'gesperrt';
      const thresholdLabel = reward.element.querySelector('[data-reward-threshold]');
      if (thresholdLabel) thresholdLabel.textContent = this.formatJourneyMoney(reward.threshold);
      const visibleState = reward.element.querySelector('[data-reward-label-state]');
      if (visibleState) {
        visibleState.textContent = reward.unavailable
          ? 'Nicht verfügbar'
          : reward.declined ? 'Erneut hinzufügen'
          : reward.unlocked ? 'Erreicht'
          : 'ab';
      }
      const state = reward.element.querySelector('[data-reward-state]');
      if (state) state.textContent = `${reward.title}: ${reward.element.dataset.rewardState}`;
      reward.element.setAttribute('aria-label', `${reward.title}, ${this.formatJourneyMoney(reward.threshold)}, ${reward.element.dataset.rewardState}`);
      progressElement.querySelector('[data-reward-milestones]')?.append(reward.element);
    });

    const progress = maxThreshold > 0
      ? Math.min(100, Math.max(0, subtotal / maxThreshold * 100))
      : 0;
    if (progressBar) {
      progressBar.setAttribute('aria-valuemax', String(maxThreshold));
      progressBar.setAttribute('aria-valuenow', String(Math.min(subtotal, maxThreshold)));
    }

    const nextReward = rewards.find((reward) => !reward.unlocked && !reward.unavailable) ||
      rewards.find((reward) => !reward.unlocked);
    this.renderRewardMessage(message, progressBar, nextReward, subtotal);

    const previousProgress = Number.isFinite(this.rewardProgress)
      ? this.rewardProgress
      : Math.min(1, Math.max(0, Number(progressElement.dataset.initialSubtotal || 0) / maxThreshold));
    const currentProgress = progress / 100;
    const motionAllowed = Boolean(
      this.isDrawerOpen &&
      this.configuration.journey?.motion !== false &&
      progressElement.dataset.motionEnabled !== 'false' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
    const shouldMove = Boolean(
      motionAllowed &&
      canDetectUnlock &&
      Math.abs(currentProgress - previousProgress) > 0.0005
    );
    const shouldPickup = Boolean(motionAllowed && animationTarget);
    const shouldDrop = Boolean(motionAllowed && droppedGifts.length);
    const movementDirection = currentProgress < previousProgress ? -1 : 1;
    const movementDuration = Math.min(4200, Math.max(1800, 1800 + Math.abs(currentProgress - previousProgress) * 3600));

    progressElement.querySelectorAll('.is-unlock-pending').forEach((element) => element.classList.remove('is-unlock-pending'));
    progressElement.querySelectorAll('.is-relock-pending').forEach((element) => element.classList.remove('is-relock-pending'));
    if (shouldPickup) animationTarget.element.classList.add('is-unlock-pending');
    if (shouldDrop) droppedGifts.forEach((reward) => reward.element.classList.add('is-relock-pending'));
    progressElement.style.setProperty('--reward-move-duration', `${movementDuration}ms`);

    this.rewardHistory = currentUnlocked;
    this.rewardProgress = currentProgress;
    this.persistRewardHistory(cart, currentUnlocked);
    this.renderCollectedRewards(
      progressElement,
      rewards,
      shouldPickup ? animationTarget.id : null,
      shouldDrop ? droppedGifts.map((reward) => reward.id) : []
    );
    this.prepareRewardJourney(
      progressElement,
      rewards,
      maxThreshold,
      currentProgress,
      previousProgress,
      shouldMove || shouldPickup || shouldDrop,
      shouldPickup ? animationTarget : null,
      shouldDrop ? droppedGifts : [],
      movementDirection,
      movementDuration
    );
  }

  initializeRewardHistory() {
    const progressElement = this.drawer?.querySelector('[data-unified-rewards-progress]');
    if (!progressElement) return;
    const subtotal = Number(progressElement.dataset.initialSubtotal || 0);
    const unlocked = new Set();
    let maxThreshold = 0;
    progressElement.querySelectorAll('[data-reward-milestone]').forEach((element) => {
      const threshold = Number(element.dataset.thresholdCents || 0);
      maxThreshold = Math.max(maxThreshold, threshold);
      if (threshold > 0 && subtotal >= threshold) unlocked.add(element.dataset.rewardId);
    });
    this.rewardHistory = unlocked;
    this.rewardProgress = maxThreshold > 0 ? Math.min(1, Math.max(0, subtotal / maxThreshold)) : 0;
  }

  rewardStorageKey(cart) {
    const token = cart?.token || this.configuration?.cartToken || 'cart';
    return `free-rewards:${token}:unlocked`;
  }

  persistRewardHistory(cart, unlocked) {
    try {
      sessionStorage.setItem(this.rewardStorageKey(cart), JSON.stringify([...unlocked]));
    } catch (error) {
      this.controller?.debug('Reward animation state could not be stored.', error);
    }
  }

  renderRewardMessage(message, progressBar, nextReward, subtotal) {
    if (!message) return;
    const journey = this.configuration.journey || {};
    message.replaceChildren();

    if (!nextReward) {
      const copy = document.createElement('span');
      copy.dataset.rewardMessageCopy = '';
      copy.textContent = journey.allUnlockedText || 'Alle Vorteile freigeschaltet';
      message.append(copy);
      progressBar?.setAttribute('aria-valuetext', copy.textContent);
      return;
    }

    if (nextReward.unavailable) {
      const copy = document.createElement('span');
      copy.dataset.rewardMessageCopy = '';
      copy.textContent = this.interpolate(this.configuration.strings.unavailable, { gift: nextReward.title });
      message.append(copy);
      progressBar?.setAttribute('aria-valuetext', copy.textContent);
      return;
    }

    const remaining = Math.max(0, nextReward.threshold - subtotal);
    const prefix = document.createElement('span');
    const amount = document.createElement('strong');
    const copy = document.createElement('span');
    prefix.dataset.rewardMessagePrefix = '';
    amount.dataset.rewardMessageAmount = '';
    copy.dataset.rewardMessageCopy = '';
    prefix.textContent = journey.remainingPrefix || 'Noch';
    amount.textContent = this.formatJourneyMoney(remaining);
    copy.textContent = nextReward.type === 'shipping'
      ? journey.nextShippingText || 'bis zum Gratisversand'
      : journey.nextGiftText || 'bis zum nächsten Geschenk';
    message.append(prefix, document.createTextNode(' '), amount, document.createTextNode(' '), copy);
    progressBar?.setAttribute('aria-valuetext', `${prefix.textContent} ${amount.textContent} ${copy.textContent}`);
  }

  prepareRewardJourney(
    progressElement,
    rewards,
    maxThreshold,
    currentProgress,
    previousProgress,
    animate,
    animationTarget,
    droppedRewards = [],
    direction = 1,
    duration = this.rewardWalkDuration(progressElement)
  ) {
    this.disconnectRewardObserver();
    this.rewardJourneyState = { progressElement, rewards, maxThreshold, currentProgress };
    const layout = () => {
      if (progressElement.querySelector('[data-reward-mascot]')?.classList.contains('is-walking')) return;
      this.layoutRewardJourney();
    };
    const journey = progressElement.querySelector('[data-reward-journey]');
    if ('ResizeObserver' in window) {
      this.rewardResizeObserver = new ResizeObserver(() => window.requestAnimationFrame(layout));
      if (journey) this.rewardResizeObserver.observe(journey);
    }

    const startLayout = (attempt = 0) => {
      if (!progressElement.isConnected || this.rewardJourneyState?.progressElement !== progressElement) return;
      const geometry = this.layoutRewardJourney({ progress: animate ? previousProgress : currentProgress, immediate: true });
      if (!geometry) {
        if (attempt < 8) {
          window.setTimeout(() => window.requestAnimationFrame(() => startLayout(attempt + 1)), 60);
        } else {
          progressElement.classList.remove('is-layout-pending');
        }
        return;
      }
      progressElement.classList.remove('is-layout-pending');
      if (!animate) return;
      window.requestAnimationFrame(() => {
        this.layoutRewardJourney({ progress: currentProgress, immediate: false });
        this.runRewardJourneyAnimation(progressElement, {
          reward: animationTarget,
          droppedRewards,
          direction,
          duration
        });
      });
    };
    window.requestAnimationFrame(() => startLayout());
  }

  layoutRewardJourney({ progress = this.rewardJourneyState?.currentProgress || 0, immediate = true } = {}) {
    const state = this.rewardJourneyState;
    const journey = state?.progressElement?.querySelector('[data-reward-journey]');
    const path = journey?.querySelector('[data-reward-path]');
    const progressPath = journey?.querySelector('[data-reward-progress-path]');
    const progressClip = journey?.querySelector('[data-reward-progress-clip]');
    const mascot = journey?.querySelector('[data-reward-mascot]');
    const svg = path?.ownerSVGElement;
    const viewBox = svg?.viewBox?.baseVal;
    if (!journey || !path || !progressPath || !progressClip || !mascot || !path.getTotalLength() || !viewBox?.width || !viewBox?.height) return null;

    const journeyRect = journey.getBoundingClientRect();
    if (!journeyRect.width || !journeyRect.height) return null;
    const length = path.getTotalLength();
    const pointAt = (fraction) => {
      const normalized = Math.min(1, Math.max(0, fraction));
      const point = path.getPointAtLength(length * normalized);
      const next = path.getPointAtLength(Math.min(length, length * normalized + 1));
      const screenPoint = {
        x: (point.x - viewBox.x) / viewBox.width * journeyRect.width,
        y: (point.y - viewBox.y) / viewBox.height * journeyRect.height
      };
      const screenNext = {
        x: (next.x - viewBox.x) / viewBox.width * journeyRect.width,
        y: (next.y - viewBox.y) / viewBox.height * journeyRect.height
      };
      return {
        x: screenPoint.x,
        y: screenPoint.y,
        angle: Math.max(-3, Math.min(3, Math.atan2(screenNext.y - screenPoint.y, screenNext.x - screenPoint.x) * 180 / Math.PI))
      };
    };

    const rawFractions = state.rewards.map((reward) =>
      state.maxThreshold > 0 ? Math.min(1, Math.max(0, reward.threshold / state.maxThreshold)) : 0
    );
    const visualFractions = [...rawFractions];
    const minimumMilestoneGap = Math.min(0.2, Math.max(0.075, 42 / Math.max(1, journeyRect.width)));
    const minimumStartGap = Math.min(0.24, Math.max(0.12, 52 / Math.max(1, journeyRect.width)));
    if (visualFractions.length) visualFractions[0] = Math.max(visualFractions[0], minimumStartGap);
    for (let index = 1; index < visualFractions.length; index += 1) {
      visualFractions[index] = Math.max(visualFractions[index], visualFractions[index - 1] + minimumMilestoneGap);
    }
    if (visualFractions.at(-1) > 1) {
      visualFractions[visualFractions.length - 1] = 1;
      for (let index = visualFractions.length - 2; index >= 0; index -= 1) {
        visualFractions[index] = Math.min(visualFractions[index], visualFractions[index + 1] - minimumMilestoneGap);
      }
    }

    state.rewards.forEach((reward, index) => {
      reward.rawFraction = rawFractions[index];
      reward.visualFraction = visualFractions[index];
      const point = pointAt(reward.visualFraction);
      reward.element.style.left = `${point.x}px`;
      reward.element.style.top = `${point.y}px`;
      reward.element.style.setProperty('--label-stagger', state.rewards.length > 3 && index % 2 ? '1.15rem' : '0rem');
    });

    const normalizedProgress = Math.min(1, Math.max(0, progress));
    const mapProgressToVisual = (value) => {
      let previousRaw = 0;
      let previousVisual = 0;
      for (let index = 0; index < rawFractions.length; index += 1) {
        const currentRaw = rawFractions[index];
        const currentVisual = visualFractions[index];
        if (value <= currentRaw) {
          const range = currentRaw - previousRaw;
          const ratio = range > 0 ? (value - previousRaw) / range : 1;
          const clampedRatio = Math.min(1, Math.max(0, ratio));
          const visualRatio = previousRaw > 0 ? Math.pow(clampedRatio, 2) : clampedRatio;
          return previousVisual + (currentVisual - previousVisual) * visualRatio;
        }
        previousRaw = currentRaw;
        previousVisual = currentVisual;
      }
      return 1;
    };

    const mappedProgress = mapProgressToVisual(normalizedProgress);
    let visualProgress = mappedProgress;
    const lastCollectedGift = [...state.rewards].reverse().find((reward) =>
      reward.type === 'gift' && reward.unlocked && !reward.unavailable && !reward.superseded && !reward.declined &&
      normalizedProgress >= reward.rawFraction
    );
    if (lastCollectedGift) {
      const milestonePoint = pointAt(lastCollectedGift.visualFraction);
      const mascotWidth = mascot.getBoundingClientRect().width || 41;
      const minimumSeparation = mascotWidth / 2 + 5;
      const nextReward = state.rewards.find((reward) => reward.visualFraction > lastCollectedGift.visualFraction);
      const maximumProgress = nextReward
        ? Math.max(lastCollectedGift.visualFraction, nextReward.visualFraction - minimumMilestoneGap * 0.42)
        : 1;
      let mascotPoint = pointAt(visualProgress);
      while (
        Math.hypot(mascotPoint.x - milestonePoint.x, mascotPoint.y - milestonePoint.y) < minimumSeparation &&
        visualProgress < maximumProgress
      ) {
        visualProgress = Math.min(maximumProgress, visualProgress + 0.008);
        mascotPoint = pointAt(visualProgress);
      }
    }

    const nextLockedReward = state.rewards.find((reward) =>
      !reward.unlocked && !reward.unavailable && reward.visualFraction >= visualProgress
    );
    if (nextLockedReward) {
      const milestonePoint = pointAt(nextLockedReward.visualFraction);
      const pinWidth = nextLockedReward.element.querySelector('.custom-cart-reward__pin')?.getBoundingClientRect().width || 34;
      const mascotWidth = mascot.getBoundingClientRect().width || 41;
      const minimumSeparation = mascotWidth / 2 + pinWidth / 2 + 6;
      const previousReward = [...state.rewards].reverse().find((reward) => reward.visualFraction < nextLockedReward.visualFraction);
      const minimumProgress = previousReward
        ? Math.min(nextLockedReward.visualFraction, previousReward.visualFraction + minimumMilestoneGap * 0.42)
        : 0;
      let mascotPoint = pointAt(visualProgress);
      while (
        Math.hypot(mascotPoint.x - milestonePoint.x, mascotPoint.y - milestonePoint.y) < minimumSeparation &&
        visualProgress > minimumProgress
      ) {
        visualProgress = Math.max(minimumProgress, visualProgress - 0.008);
        mascotPoint = pointAt(visualProgress);
      }
    }

    const point = pointAt(visualProgress);
    const mascotTransform = `translate3d(${point.x}px, ${point.y}px, 0) translate(-46%, -72%) rotate(${point.angle}deg)`;
    mascot.classList.add('is-path-ready');
    if (immediate) mascot.style.transition = 'none';
    else mascot.style.removeProperty('transition');
    mascot.style.transform = mascotTransform;

    // Keep the completed stroke endpoint on the mascot's path anchor, which is
    // visually aligned with the center of the hedgehog.
    const lineProgress = visualProgress;
    const linePoint = point;
    const clipScale = Math.min(1, Math.max(0, linePoint.x / journeyRect.width));
    progressClip.style.transition = immediate ? 'none' : '';
    progressClip.style.transform = `scaleX(${clipScale})`;
    progressPath.removeAttribute('pathLength');
    progressPath.removeAttribute('stroke-dasharray');
    progressPath.removeAttribute('stroke-dashoffset');

    const tooltip = journey.querySelector('[data-reward-tooltip]');
    if (tooltip) {
      const tooltipX = Math.min(journeyRect.width - 66, Math.max(66, point.x + 12));
      tooltip.style.setProperty('--tooltip-x', `${tooltipX}px`);
    }
    return { point, pointAt, visualProgress, lineProgress };
  }

  renderCollectedRewards(progressElement, rewards, excludedRewardId = null, includedRewardIds = []) {
    const container = progressElement.querySelector('[data-collected-rewards]');
    if (!container) return;
    const included = new Set(includedRewardIds);
    container.replaceChildren();
    rewards.filter((reward) =>
      reward.type === 'gift' &&
      ((reward.unlocked && !reward.unavailable && !reward.superseded && !reward.declined) || included.has(reward.id)) &&
      reward.id !== excludedRewardId
    ).forEach((reward) => {
      const source = reward.element.querySelector('.custom-cart-reward__pin img, .custom-cart-reward__pin svg');
      if (!source) return;
      const collected = source.cloneNode(true);
      collected.removeAttribute('width');
      collected.removeAttribute('height');
      collected.setAttribute('aria-hidden', 'true');
      collected.dataset.collectedRewardId = reward.id;
      if (collected.tagName === 'IMG') collected.alt = '';
      container.append(collected);
    });
    container.dataset.collectedCount = String(container.children.length);
  }

  async runRewardJourneyAnimation(progressElement, {
    reward = null,
    droppedRewards = [],
    direction = 1,
    duration
  } = {}) {
    this.cancelRewardAnimation();
    const hasUnlock = Boolean(reward?.element?.isConnected);
    if (hasUnlock) reward.element.classList.add('is-unlock-pending');
    droppedRewards.filter((item) => item?.element?.isConnected)
      .forEach((item) => item.element.classList.add('is-relock-pending'));
    const token = ++this.rewardAnimationToken;
    const mascot = progressElement.querySelector('[data-reward-mascot]');
    mascot?.classList.toggle('is-reversing', direction < 0);
    mascot?.classList.add('is-walking');
    await this.rewardDelay(duration || this.rewardWalkDuration(progressElement));
    if (token !== this.rewardAnimationToken || (hasUnlock && !reward.element.isConnected)) return;

    mascot?.classList.remove('is-walking');
    if (droppedRewards.length) {
      await this.dropRewardsFromMascot(progressElement, droppedRewards, token);
      if (token !== this.rewardAnimationToken) return;
      droppedRewards.forEach((item) => item.element?.classList.remove('is-relock-pending'));
      this.renderCollectedRewards(progressElement, this.rewardJourneyState?.rewards || []);
    }
    if (!hasUnlock) {
      this.layoutRewardJourney({ progress: this.rewardJourneyState?.currentProgress || 0, immediate: true });
      return;
    }
    mascot?.classList.add('is-arriving');
    reward.element.classList.add('is-arriving');

    if (reward.type === 'gift') await this.flyRewardToMascot(progressElement, reward, token);
    if (token !== this.rewardAnimationToken) return;

    reward.element.classList.remove('is-unlock-pending');
    this.renderCollectedRewards(progressElement, this.rewardJourneyState?.rewards || []);
    this.layoutRewardJourney({ progress: this.rewardJourneyState?.currentProgress || 0, immediate: true });
    const journey = progressElement.querySelector('[data-reward-journey]');
    const tooltip = journey?.querySelector('[data-reward-tooltip]');
    const sparkles = journey?.querySelector('[data-reward-sparkles]');
    const milestoneRect = reward.element.querySelector('.custom-cart-reward__pin')?.getBoundingClientRect();
    const journeyRect = journey?.getBoundingClientRect();
    if (sparkles && milestoneRect && journeyRect && progressElement.dataset.sparklesEnabled !== 'false') {
      sparkles.style.setProperty('--sparkle-x', `${milestoneRect.left + milestoneRect.width / 2 - journeyRect.left}px`);
      sparkles.style.setProperty('--sparkle-y', `${milestoneRect.top + milestoneRect.height / 2 - journeyRect.top}px`);
      sparkles.classList.add('is-visible');
    }
    tooltip?.classList.add('is-visible');
    tooltip?.setAttribute('aria-hidden', 'false');
    const liveRegion = progressElement.querySelector('[data-reward-live-region]');
    if (liveRegion) liveRegion.textContent = `${this.configuration.journey?.unlockHeading || 'Geschenk freigeschaltet!'} ${this.configuration.journey?.unlockText || 'Automatisch hinzugefügt'}`;

    await this.rewardDelay(1200);
    if (token !== this.rewardAnimationToken) return;
    tooltip?.classList.remove('is-visible');
    tooltip?.setAttribute('aria-hidden', 'true');
    sparkles?.classList.remove('is-visible');
    mascot?.classList.remove('is-arriving');
    reward.element.classList.remove('is-arriving');
    this.layoutRewardJourney({ progress: this.rewardJourneyState?.currentProgress || 0, immediate: true });
  }

  async dropRewardsFromMascot(progressElement, rewards, token) {
    const container = progressElement.querySelector('[data-collected-rewards]');
    if (!container || typeof Element.prototype.animate !== 'function') return;
    const animations = rewards.map((reward, index) => {
      const escapedId = window.CSS?.escape ? window.CSS.escape(reward.id) : reward.id.replace(/[^a-zA-Z0-9_-]/g, '');
      const source = container.querySelector(`[data-collected-reward-id="${escapedId}"]`);
      if (!source) return null;
      const sourceRect = source.getBoundingClientRect();
      const clone = source.cloneNode(true);
      clone.className = 'free-rewards-drop';
      clone.removeAttribute('data-collected-reward-id');
      clone.setAttribute('aria-hidden', 'true');
      clone.style.left = `${sourceRect.left}px`;
      clone.style.top = `${sourceRect.top}px`;
      clone.style.width = `${sourceRect.width}px`;
      clone.style.height = `${sourceRect.height}px`;
      document.body.append(clone);
      source.style.opacity = '0';
      const drift = (index % 2 ? 1 : -1) * (15 + index * 5);
      const animation = clone.animate([
        { transform: 'translate3d(0, 0, 0) rotate(0deg)', opacity: 1 },
        { transform: `translate3d(${drift * 0.45}px, 0.5rem, 0) rotate(${drift * 0.7}deg)`, opacity: 1, offset: 0.34 },
        { transform: `translate3d(${drift}px, 4.2rem, 0) rotate(${drift * 2.2}deg)`, opacity: 0 }
      ], {
        duration: 760 + index * 90,
        easing: 'cubic-bezier(0.32, 0, 0.67, 1)',
        fill: 'forwards'
      });
      this.activeRewardAnimations.add(animation);
      const safetyTimeout = this.rewardDelay(1100 + index * 90);
      return Promise.race([animation.finished.catch(() => undefined), safetyTimeout]).finally(() => {
        animation.cancel();
        this.activeRewardAnimations.delete(animation);
        clone.remove();
      });
    }).filter(Boolean);
    await Promise.all(animations);
    if (token !== this.rewardAnimationToken) return;
  }

  async flyRewardToMascot(progressElement, reward, token) {
    const source = reward.element.querySelector('.custom-cart-reward__pin');
    const target = progressElement.querySelector('[data-reward-back-anchor]');
    if (!source || !target || typeof source.animate !== 'function') return;
    const sourceRect = source.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const clone = source.cloneNode(true);
    clone.className = 'free-rewards-flight';
    clone.querySelectorAll('[alt]').forEach((element) => element.setAttribute('alt', ''));
    clone.setAttribute('aria-hidden', 'true');
    clone.style.left = `${sourceRect.left}px`;
    clone.style.top = `${sourceRect.top}px`;
    clone.style.color = getComputedStyle(progressElement).getPropertyValue('--free-rewards-text');
    clone.style.background = getComputedStyle(progressElement).getPropertyValue('--free-rewards-background');
    document.body.append(clone);
    reward.element.classList.remove('is-unlock-pending');

    const deltaX = targetRect.left + targetRect.width / 2 - (sourceRect.left + sourceRect.width / 2);
    const deltaY = targetRect.top + targetRect.height / 2 - (sourceRect.top + sourceRect.height / 2);
    const animation = clone.animate([
      { transform: 'translate3d(0, 0, 0) scale(0.85)', opacity: 1 },
      { transform: `translate3d(${deltaX * 0.48}px, ${deltaY * 0.48 - 28}px, 0) scale(0.95)`, opacity: 1, offset: 0.5 },
      { transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scale(0.34)`, opacity: 0.96 }
    ], { duration: 620, easing: 'cubic-bezier(0.22, 0.72, 0.28, 1)', fill: 'forwards' });
    this.activeRewardAnimations.add(animation);
    try {
      await animation.finished;
    } catch (error) {
      this.controller?.debug('Reward pickup animation was interrupted.', error);
    } finally {
      this.activeRewardAnimations.delete(animation);
      clone.remove();
    }
    if (token !== this.rewardAnimationToken) return;
  }

  rewardDelay(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  rewardWalkDuration(progressElement) {
    const value = getComputedStyle(progressElement).getPropertyValue('--free-rewards-walk-duration').trim();
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return 2400;
    const milliseconds = value.endsWith('ms') ? parsed : parsed * 1000;
    return Math.min(5000, Math.max(400, milliseconds));
  }

  cancelRewardAnimation() {
    this.rewardAnimationToken += 1;
    this.activeRewardAnimations.forEach((animation) => animation.cancel());
    this.activeRewardAnimations.clear();
    document.querySelectorAll('.free-rewards-flight').forEach((element) => element.remove());
    this.drawer?.querySelectorAll('.is-walking, .is-arriving, .is-unlock-pending, .is-relock-pending, [data-reward-tooltip].is-visible, [data-reward-sparkles].is-visible')
      .forEach((element) => element.classList.remove('is-walking', 'is-arriving', 'is-unlock-pending', 'is-relock-pending', 'is-visible'));
    this.drawer?.querySelectorAll('[data-reward-tooltip]').forEach((element) => element.setAttribute('aria-hidden', 'true'));
  }

  disconnectRewardObserver() {
    this.rewardResizeObserver?.disconnect();
    this.rewardResizeObserver = null;
  }

  updateDebugUI(result) {
    const output = this.drawer?.querySelector('[data-gift-debug-content]');
    if (!output || !result?.state || !this.controller) return;
    const { cart, state } = result;
    const unavailableIds = new Set(result.unavailableVariantIds || []);
    const lines = [
      `Campaign: ${this.configuration.enabled ? 'ON' : 'OFF'} | Auto-add: ${this.configuration.autoAdd ? 'ON' : 'OFF'} | Mode: ${this.configuration.mode} | Paid-test override: ${this.configuration.debug ? 'ON' : 'OFF'}`,
      `Cart total: ${this.formatMoney(cart.total_price)} | Qualifying subtotal: ${this.formatMoney(state.subtotal)}`
    ];

    this.controller.tiers.forEach((tier) => {
      const threshold = this.controller.thresholdFor(tier, cart);
      const giftLine = (cart.items || []).find((item) =>
        item.properties?._free_gift === 'true' &&
        item.properties?._gift_campaign === this.configuration.campaignKey &&
        String(item.properties?._gift_tier || '') === tier.key
      );
      const declined = Boolean((cart.attributes || {})[this.controller.declineAttributeKey(tier.key)]);
      const paidTest = Boolean(this.configuration.debug && tier.freeVariantPrice > 0);
      let status;

      if (!tier.valid) {
        status = `INVALID — ${tier.errors.map((error) => this.controller.describeError(error, tier)).join(' ')}`;
      } else if (unavailableIds.has(tier.freeVariantId) || tier.available === false) {
        status = 'UNAVAILABLE / sold out';
      } else if (declined) {
        status = 'DECLINED by customer';
      } else if (giftLine) {
        status = paidTest ? 'ADDED as PAID TEST GIFT' : 'ADDED to cart';
      } else if (state.subtotal >= threshold) {
        status = paidTest ? 'UNLOCKED — paid test gift is pending' : 'UNLOCKED, but no gift line is present';
      } else {
        status = `LOCKED — needs ${this.formatMoney(threshold - state.subtotal)} more`;
      }
      const giftPrice = tier.freeVariantPrice >= 0 ? this.formatMoney(tier.freeVariantPrice) : 'n/a';

      lines.push(
        '',
        `Tier ${tier.key}: ${tier.title || 'Gift'} — ${status}`,
        `Product: ${tier.productTitle || tier.productId || 'not selected'}`,
        `Setup: ${tier.setup} | variants: ${tier.variantCount} | selected gift price: ${giftPrice} | threshold: ${this.formatMoney(threshold)}`,
        paidTest ? 'Debug override: the first available variant is used and WILL increase the cart total.' : ''
      );
    });

    output.textContent = lines.join('\n');
  }

  convertThreshold(thresholdCents, cart) {
    if (!cart?.currency || cart.currency === this.configuration.shopCurrency) return thresholdCents;
    const rate = Number(window.Shopify?.currency?.rate);
    return Number.isFinite(rate) && rate > 0 ? Math.round(thresholdCents * rate) : thresholdCents;
  }

  interpolate(template = '', replacements = {}) {
    return Object.entries(replacements).reduce((message, [key, value]) =>
      message
        .split(`[${key}]`).join(value)
        .split(`{{ ${key} }}`).join(value)
        .split(`{{${key}}}`).join(value), String(template));
  }

  formatMoney(cents) {
    const format = this.configuration?.moneyFormat || '${{amount}}';
    const placeholder = format.match(/{{\s*(amount(?:_no_decimals|_with_comma_separator|_no_decimals_with_comma_separator)?)\s*}}/);
    if (!placeholder) return String((Number(cents || 0) / 100).toFixed(2));

    const formatWithDelimiters = (value, precision = 2, thousands = ',', decimal = '.') => {
      const number = Number(value) / 100;
      if (!Number.isFinite(number)) return '0';
      const parts = number.toFixed(precision).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? parts.join(decimal) : parts[0];
    };

    const values = {
      amount: formatWithDelimiters(cents, 2),
      amount_no_decimals: formatWithDelimiters(cents, 0),
      amount_with_comma_separator: formatWithDelimiters(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: formatWithDelimiters(cents, 0, '.', ',')
    };
    return format.replace(placeholder[0], values[placeholder[1]]);
  }

  formatJourneyMoney(cents) {
    try {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: this.configuration?.shopCurrency || 'EUR',
        currencyDisplay: 'narrowSymbol'
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return this.formatMoney(cents);
    }
  }

  async changeLine(lineKey, quantity, control) {
    this.setControlBusy(control, true);
    try {
      const cart = await window.customCartAPI.change(lineKey, Math.max(0, Number(quantity)));
      await this.sync({ cart, render: true, preserveOpen: this.isDrawerOpen });
      this.announce(this.configuration.strings.updated);
    } finally {
      this.desiredQuantities.delete(lineKey);
      this.setControlBusy(control, false);
    }
  }

  async addPaidUnit(tierKey, control) {
    const tier = this.controller.tiers.find((candidate) => candidate.key === String(tierKey));
    if (!tier?.valid || !tier.paidAvailable) throw new Error(this.configuration.strings.paidUnavailable);

    this.setControlBusy(control, true);
    try {
      this.controller.debug(`Adding paid unit for gift tier ${tier.key}.`, {
        paidVariantId: tier.paidVariantId,
        paidVariantPrice: tier.paidVariantPrice
      });
      await window.customCartAPI.addItems([{
        id: tier.paidVariantId,
        quantity: 1,
        properties: {
          _gift_additional: 'true',
          _gift_tier: tier.key,
          _gift_campaign: this.configuration.campaignKey
        }
      }]);
      await this.sync({ render: true, preserveOpen: this.isDrawerOpen });
      this.controller.debug(`Paid unit for gift tier ${tier.key} was added as a separate cart line.`);
      this.announce(this.configuration.strings.updated);
    } finally {
      this.setControlBusy(control, false);
    }
  }

  async removeGift(lineKey, tierKey, control) {
    if (!this.configuration.allowGiftRemoval) return;
    this.setControlBusy(control, true);

    try {
      if (this.configuration.removalBehavior === 'respect') {
        const attributeKey = this.controller.declineAttributeKey(String(tierKey));
        await window.customCartAPI.update({ attributes: { [attributeKey]: 'true' } });
      }
      const cart = await window.customCartAPI.change(lineKey, 0);
      await this.sync({ cart, render: true, preserveOpen: this.isDrawerOpen });
      this.announce(this.configuration.strings.updated);
    } finally {
      this.setControlBusy(control, false);
    }
  }

  async claimGift(tierKey, control) {
    const tier = this.controller.tiers.find((candidate) => candidate.key === String(tierKey));
    if (!tier?.valid || tier.available === false) throw new Error(this.configuration.strings.unavailable);
    this.setControlBusy(control, true);

    const attributeKey = this.controller.declineAttributeKey(tier.key);
    try {
      const cart = await window.customCartAPI.fetchCart();
      const state = this.controller.getState(cart);
      const canClaim = state.expectedTiers.some((candidate) => candidate.key === tier.key);
      if (!canClaim) throw new Error(tier.lockedMessage || this.configuration.strings.locked);

      await window.customCartAPI.update({ attributes: { [attributeKey]: '' } });
      const alreadyPresent = (cart.items || []).some((item) =>
        item.properties?._free_gift === 'true' &&
        item.properties?._gift_campaign === this.configuration.campaignKey &&
        String(item.properties?._gift_tier || '') === tier.key
      );

      if (!alreadyPresent) {
        const properties = {
          _free_gift: 'true',
          _gift_tier: tier.key,
          _gift_campaign: this.configuration.campaignKey
        };
        if (this.configuration.debug && tier.freeVariantPrice > 0) properties._gift_debug_paid = 'true';
        await window.customCartAPI.addItems([{ id: tier.freeVariantId, quantity: 1, properties }]);
      }

      await this.sync({ render: true, preserveOpen: this.isDrawerOpen });
      this.announce(this.configuration.strings.added);
    } catch (error) {
      await window.customCartAPI.update({ attributes: { [attributeKey]: 'true' } }).catch(() => undefined);
      throw error;
    } finally {
      this.setControlBusy(control, false);
    }
  }

  async addUpsellProduct(control) {
    const variantId = Number(control?.dataset.variantId || 0);
    if (!(variantId > 0)) throw new Error(this.configuration?.strings?.error || 'This product is unavailable.');
    const isProtectedGiftVariant = this.controller?.tiers?.some((tier) => Number(tier.freeVariantId) === variantId);
    if (isProtectedGiftVariant) throw new Error(this.configuration?.strings?.paidUnavailable || 'This option is unavailable.');

    const card = control.closest('[data-upsell-card]') || this.activeUpsellCard;
    this.setControlBusy(control, true);
    card?.classList.add('is-loading');
    try {
      await window.customCartAPI.addItems([{
        id: variantId,
        quantity: 1,
        properties: {
          '_Added via': 'Cart Upsell'
        }
      }]);
      this.closeUpsellPicker({ restoreFocus: false });
      await this.sync({ render: true, preserveOpen: this.isDrawerOpen });
      this.announce(this.configuration?.strings?.upsellAdded || this.configuration?.strings?.added);
    } finally {
      card?.classList.remove('is-loading');
      this.setControlBusy(control, false);
    }
  }

  openUpsellPicker(control) {
    const card = control?.closest('[data-upsell-card]');
    const picker = card?.querySelector('[data-upsell-picker]');
    if (!picker) return;

    this.closeUpsellPicker({ restoreFocus: false });
    this.activeUpsellPicker = picker;
    this.activeUpsellCard = card;
    this.upsellPickerOpener = control;
    picker.hidden = false;
    picker.setAttribute('aria-hidden', 'false');
    this.panel?.appendChild(picker);
    document.body.classList.add('custom-cart-upsell-picker-open');
    window.requestAnimationFrame(() => {
      picker.classList.add('is-open');
      const selectedOption = picker.querySelector('[data-upsell-option][aria-checked="true"]');
      (selectedOption || picker.querySelector('[data-upsell-picker-close]'))?.focus();
    });
  }

  closeUpsellPicker({ restoreFocus = true } = {}) {
    const picker = this.activeUpsellPicker;
    if (!picker) return;
    const card = this.activeUpsellCard;
    picker.classList.remove('is-open');
    picker.hidden = true;
    picker.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('custom-cart-upsell-picker-open');
    const opener = this.upsellPickerOpener;
    this.activeUpsellPicker = null;
    this.activeUpsellCard = null;
    this.upsellPickerOpener = null;
    if (card?.isConnected) card.appendChild(picker);
    if (restoreFocus && opener?.isConnected) opener.focus();
  }

  selectUpsellOption(option) {
    const picker = option?.closest('[data-upsell-picker]');
    if (!picker) return;
    picker.querySelectorAll('[data-upsell-option]').forEach((candidate) => {
      candidate.setAttribute('aria-checked', String(candidate === option));
    });

    const confirm = picker.querySelector('[data-cart-action="upsell-add"]');
    const selection = picker.querySelector('[data-upsell-picker-selection]');
    const price = picker.querySelector('[data-upsell-picker-price]');
    const image = picker.querySelector('.custom-cart-upsell-picker__media img');
    if (confirm) confirm.dataset.variantId = option.dataset.variantId || '';
    if (selection) selection.textContent = option.dataset.variantTitle || '';
    if (price) price.textContent = option.dataset.price || '';
    if (image && option.dataset.imageUrl) {
      image.src = option.dataset.imageUrl;
      image.removeAttribute('srcset');
    }
  }

  async checkout(form) {
    if (this.checkoutInProgress) return;
    this.checkoutInProgress = true;
    this.drawer?.setAttribute('aria-busy', 'true');

    const formId = form?.id;
    try {
      await this.sync({ render: false, preserveOpen: this.isDrawerOpen });
      const checkoutForm = (formId && document.getElementById(formId)) ||
        document.getElementById('CustomCartCheckoutForm') || document.getElementById('cart');
      if (!checkoutForm) return;

      if (!checkoutForm.querySelector('input[type="hidden"][name="checkout"]')) {
        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = 'Checkout';
        checkoutForm.appendChild(checkoutInput);
      }
      HTMLFormElement.prototype.submit.call(checkoutForm);
    } catch (error) {
      this.checkoutInProgress = false;
      this.drawer?.removeAttribute('aria-busy');
      this.handleError(error);
    }
  }

  onDocumentClick(event) {
    const opener = event.target.closest('[data-cart-drawer-open], #cart-icon-bubble');
    if (opener && this.drawer) {
      event.preventDefault();
      this.open(opener);
      return;
    }

    if (event.target.closest('[data-upsell-picker-close]')) {
      event.preventDefault();
      this.closeUpsellPicker();
      return;
    }

    const upsellOption = event.target.closest('[data-upsell-option]');
    if (upsellOption) {
      event.preventDefault();
      this.selectUpsellOption(upsellOption);
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    const upsellScroll = event.target.closest('[data-upsell-scroll]');
    if (upsellScroll) {
      event.preventDefault();
      const track = upsellScroll.closest('[data-cart-upsells]')?.querySelector('[data-upsell-track]');
      if (track) {
        const direction = upsellScroll.dataset.upsellScroll === 'previous' ? -1 : 1;
        track.scrollBy({ left: direction * Math.max(220, track.clientWidth * 0.82), behavior: 'smooth' });
      }
      return;
    }

    const checkoutButton = event.target.closest('[data-cart-checkout], #checkout');
    if (checkoutButton) {
      event.preventDefault();
      this.enqueueMutation(() => this.checkout(checkoutButton.form));
      return;
    }

    const control = event.target.closest('[data-cart-action]');
    if (!control || control.disabled || control.getAttribute('aria-disabled') === 'true') return;
    event.preventDefault();
    const action = control.dataset.cartAction;

    if (action === 'change') {
      const lineKey = control.dataset.lineKey;
      const current = this.desiredQuantities.has(lineKey)
        ? this.desiredQuantities.get(lineKey)
        : Number(control.dataset.currentQuantity || 0);
      const desired = Math.max(0, current + Number(control.dataset.delta || 0));
      this.desiredQuantities.set(lineKey, desired);
      this.enqueueMutation(() => this.changeLine(lineKey, desired, control)).catch((error) => this.handleError(error, control));
    } else if (action === 'remove') {
      this.enqueueMutation(() => this.changeLine(control.dataset.lineKey, 0, control)).catch((error) => this.handleError(error, control));
    } else if (action === 'add-paid') {
      this.enqueueMutation(() => this.addPaidUnit(control.dataset.tierKey, control)).catch((error) => this.handleError(error, control));
    } else if (action === 'remove-gift') {
      this.enqueueMutation(() => this.removeGift(control.dataset.lineKey, control.dataset.tierKey, control))
        .catch((error) => this.handleError(error, control));
    } else if (action === 'claim-gift') {
      this.enqueueMutation(() => this.claimGift(control.dataset.tierKey, control))
        .catch((error) => this.handleError(error, control));
    } else if (action === 'upsell-open') {
      this.openUpsellPicker(control);
    } else if (action === 'upsell-add') {
      this.enqueueMutation(() => this.addUpsellProduct(control))
        .catch((error) => this.handleError(error, control));
    }
  }

  onDocumentChange(event) {
    const upsellVariant = event.target.closest('[data-upsell-variant-select]');
    if (upsellVariant) {
      const card = upsellVariant.closest('[data-upsell-card]');
      const selected = upsellVariant.selectedOptions[0];
      const button = card?.querySelector('[data-cart-action="upsell-add"]');
      const price = card?.querySelector('[data-upsell-price]');
      const comparePrice = card?.querySelector('[data-upsell-compare-price]');
      if (button) button.dataset.variantId = selected?.value || '';
      if (price && selected?.dataset.price) price.textContent = selected.dataset.price;
      if (comparePrice) {
        comparePrice.textContent = selected?.dataset.comparePrice || '';
        comparePrice.hidden = !selected?.dataset.comparePrice;
      }
      if (selected?.dataset.imageUrl) {
        const image = card?.querySelector('.custom-cart-upsell-card__media img');
        if (image) {
          image.src = selected.dataset.imageUrl;
          image.removeAttribute('srcset');
        }
      }
      return;
    }

    const quantityInput = event.target.closest('[data-cart-quantity-input]');
    if (quantityInput) {
      const quantity = Math.max(0, Number(quantityInput.value || 0));
      this.enqueueMutation(() => this.changeLine(quantityInput.dataset.lineKey, quantity, quantityInput))
        .catch((error) => this.handleError(error, quantityInput));
      return;
    }

    const note = event.target.closest('[data-cart-note]');
    if (note) {
      window.clearTimeout(this.noteTimer);
      this.noteTimer = window.setTimeout(() => {
        this.enqueueMutation(async () => {
          await window.customCartAPI.update({ note: note.value });
          this.announce(this.configuration.strings.noteUpdated);
        }).catch((error) => this.handleError(error, note));
      }, 300);
    }
  }

  onDocumentKeydown(event) {
    if (event.key === 'Escape' && this.activeUpsellPicker) {
      event.preventDefault();
      this.closeUpsellPicker();
      return;
    }
    if (event.key === 'Escape' && this.isDrawerOpen) {
      event.preventDefault();
      this.close();
    }
  }

  onItemAdded(event) {
    this.returnFocusElement = event.detail?.source || document.activeElement;
    this.setOpenState(true, { focus: false });
    this.enqueueMutation(async () => {
      await this.sync({ render: true, preserveOpen: true });
      this.focusDrawer();
      this.announce(this.configuration.strings.added);
    }).catch((error) => this.handleError(error));
  }

  onExternalCartUpdate(event) {
    if (event.detail?.source === 'custom-cart-drawer') return;
    this.enqueueMutation(() => this.sync({ cart: event.detail?.cart || null, render: true }))
      .catch((error) => this.handleError(error));
  }

  onPageShow(event) {
    if (!event.persisted) return;
    this.enqueueMutation(() => this.sync({ render: true, preserveOpen: false }))
      .catch((error) => this.handleError(error));
  }

  onSectionLoad(event) {
    if (!window.Shopify?.designMode) return;
    if (!event.target?.querySelector?.('[data-custom-cart-drawer]') && event.target?.id !== `shopify-section-${this.sectionId}`) return;
    if (event.type === 'shopify:section:unload') {
      this.setOpenState(false, { restoreFocus: false });
      return;
    }
    this.refreshDOMReferences();
    this.configure();
    this.enqueueMutation(() => this.sync({ render: false, preserveOpen: this.isDrawerOpen }))
      .catch((error) => this.handleError(error));
  }

  setControlBusy(control, busy) {
    if (!control?.isConnected) return;
    const item = control.closest('[data-cart-item]');
    control.toggleAttribute('disabled', busy);
    control.setAttribute('aria-busy', String(busy));
    if (item) item.classList.toggle('is-loading', busy);
  }

  announce(message) {
    this.refreshDOMReferences();
    if (!this.liveRegion || !message) return;
    this.liveRegion.textContent = message;
  }

  handleError(error, control = null) {
    this.refreshDOMReferences();
    const message = error?.message || this.configuration?.strings?.error || window.cartStrings?.error || '';
    const line = control?.closest('[data-cart-item]');
    const lineError = line?.querySelector('[data-line-error]');
    if (lineError) lineError.textContent = message;
    if (this.errorRegion) {
      this.errorRegion.hidden = false;
      this.errorRegion.textContent = message;
    }
    this.announce(message);
    if (this.configuration?.debug) console.error('[Cart drawer]', error);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (window.CartDrawer) return;
  const app = new CustomCartDrawerApp();
  /* Public API: call window.CartDrawer.open(), close(), refresh(), or dispatch `cart-drawer:open`. */
  window.CartDrawer = {
    open: (trigger) => app.open(trigger),
    close: () => app.close(),
    refresh: () => app.refresh(),
    reconcile: () => app.reconcile(),
    removeGift: (lineKey, tierKey, control) => app.enqueueMutation(() => app.removeGift(lineKey, tierKey, control)),
    get app() { return app; }
  };
});
